import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDropiIntegrations,
  getIntegrationsAvailability,
  getMastershopIntegrations,
} from "../../../services/integrations/integrationsService";
import {
  getAllPaymentMethods,
  getPaymentMethodConfiguration,
} from "../../../services/payment/payment";

import {
  IntegrationUIConfig,
  IntegrationType,
  INTEGRATIONS_UI_CONFIG,
  CHAT_CHANNEL_TYPES,
} from "../config/integrations.config";
import { IntegrationAvailabilityApi } from "../config/integrations.types";
import { resolveAvailableIntegrations } from "../helpers/resolveAvailableIntegrations";
import { PaymentMethodStatus } from "../../payment/PaymentMethods/PaymentMethodsInterface";
import {
  DropiIntegrationInterface,
  MastershopIntegrationInterface,
} from "../../../services/integrations/integrationsService";
import { Country } from "../../../const/countryList";
import { getMetaIntegration } from "../../../services/meta/metaService";
import { userExperimentsService } from "../../../services/userExperiments/userExperimentsService";
import { getConfioIntegration } from "../../../services/confio/confioService";
import {
  chatbotV2Service,
  type ChannelAccountStatus,
} from "../../../services/chatbotV2/chatbotV2Service";
import { normalizeChannelList } from "../../../utils/normalizeChannelsOverview";
import { decodeTokenService } from "../../../services/decodeTokenService";
import { cachedRequest } from "../../../utils/requestCache";

const INTEGRATIONS_TTL_MS = 60_000;

const INTEGRATION_FLAG_MAP: Partial<Record<IntegrationType, string>> = {
  meta: "meta_campaigns_module",
  confio: "confio_payments_integration",
  mastershop: "mastershop",
};

type ExperimentsResponse = Awaited<ReturnType<typeof userExperimentsService.getExperiments>>;
type IntegrationCardState = IntegrationUIConfig & {
  isActive: boolean;
  isConnected: boolean;
  alreadyConfigured: boolean;
};

const integrationsSnapshot = new Map<string, IntegrationCardState[]>();

const getIntegrationsCacheKey = (country: Country, ownerId: string | null) =>
  `integrations:${ownerId || "anonymous"}:${country}`;

const isFlagEnabled = (flagKey: string, experiments: ExperimentsResponse | null): boolean => {
  return experiments?.experiments?.[flagKey]?.enabled === "true";
};

const isIntegrationVisible = (id: IntegrationType, experiments: ExperimentsResponse | null): boolean => {
  const flag = INTEGRATION_FLAG_MAP[id];
  if (!flag) return true;
  return isFlagEnabled(flag, experiments);
};

const resolveConfioState = (confioIntegration: Awaited<ReturnType<typeof getConfioIntegration>> | null) => {
  const connected = confioIntegration?.connected === true || confioIntegration?.active === true;
  const active = confioIntegration?.active === true || connected;

  return {
    isActive: active,
    isConnected: connected,
    alreadyConfigured: connected,
  };
};

const resolveChannelState = (channelStatus?: ChannelAccountStatus | null) => {
  const isActive = channelStatus?.is_active ?? false;
  const exists = channelStatus?.exists ?? false;
  const connected = exists && isActive && channelStatus?.status === "connected";
  return {
    isActive: connected,
    isConnected: connected,
    alreadyConfigured: connected,
  };
};

export const clearIntegrationsSnapshot = () => {
  integrationsSnapshot.clear();
};

export const useIntegrations = (country: Country = Country.COLOMBIA) => {
  const ownerId = decodeTokenService.getUserIdFromToken();
  const cacheKey = getIntegrationsCacheKey(country, ownerId);
  const snapshot = integrationsSnapshot.get(cacheKey);
  const [integrationsDropi, setIntegrationsDropi] =
    useState<DropiIntegrationInterface>();
  const [integrationsMastershop, setIntegrationsMastershop] =
    useState<MastershopIntegrationInterface>();
  const methodsStatus = useRef<PaymentMethodStatus[]>([]);
  const [loading, setLoading] = useState(!snapshot);
  const [integrations, setIntegrations] = useState<IntegrationCardState[]>(snapshot ?? []);

  const resolveIntegrationState = useCallback((
    type: IntegrationType,
    dropiData?: DropiIntegrationInterface,
    mastershopData?: MastershopIntegrationInterface
  ) => {
    if (type === "dropi") {
      const dropiIntegration = dropiData ?? integrationsDropi;
      const isActive = dropiIntegration?.is_active ?? false;
      return {
        isActive,
        isConnected: isActive,
        alreadyConfigured: isActive,
      };
    }

    if (type === "mastershop") {
      const mastershopIntegration = mastershopData ?? integrationsMastershop;
      const isActive = mastershopIntegration?.is_active ?? false;
      const isConnected = mastershopIntegration?.connected ?? isActive;
      return {
        isActive,
        isConnected,
        alreadyConfigured: isConnected,
      };
    }

    const method = methodsStatus.current.find((m) => m.id === type);
    const active = method?.active ?? false;
    const alreadyConfigured = method?.already_configured ?? false;

    return {
      isActive: active && alreadyConfigured,
      isConnected: active && alreadyConfigured,
      alreadyConfigured,
    };
  }, [integrationsDropi, integrationsMastershop]);

  useEffect(() => {
    const load = async () => {
      if (!integrationsSnapshot.get(cacheKey)) {
        setLoading(true);
      }

      try {
        const results = await cachedRequest(cacheKey, INTEGRATIONS_TTL_MS, () =>
          Promise.allSettled([
            getIntegrationsAvailability(),
            getDropiIntegrations(),
            getMastershopIntegrations(),
            getAllPaymentMethods(),
            getPaymentMethodConfiguration(),
            chatbotV2Service.getChannels(),
            getMetaIntegration(),
            userExperimentsService.getExperiments(),
            getConfioIntegration(),
          ])
        );

        const availabilityApi = results[0].status === "fulfilled"
          ? results[0].value
          : null;

        const dropiIntegration = results[1].status === "fulfilled"
          ? results[1].value
          : undefined;

        const mastershopIntegration = results[2].status === "fulfilled"
          ? results[2].value
          : undefined;

        const paymentConfig = results[4].status === "fulfilled"
          ? results[4].value
          : [];

        const channelsOverview = results[5].status === "fulfilled" ? results[5].value : null;
        const metaIntegration = results[6].status === "fulfilled" ? results[6].value : null;
        const experiments = results[7].status === "fulfilled" ? results[7].value : null;
        const confioIntegration = results[8].status === "fulfilled" ? results[8].value : null;

        results.forEach((result, index) => {
          if (result.status === "rejected") {
            const endpointNames = [
              "getIntegrationsAvailability",
              "getDropiIntegrations",
              "getMastershopIntegrations",
              "getAllPaymentMethods",
              "getPaymentMethodConfiguration",
              "getChannels",
              "getMetaIntegration",
              "getExperiments",
              "getConfioIntegration",
            ] as const;
            const error = result.reason;
            const is404 = error?.response?.status === 404;

            if (is404) {
              console.warn(`${endpointNames[index]} returned 404 (expected for some users)`);
            } else {
              console.error(`Error in ${endpointNames[index]}:`, error);
            }
          }
        });

        if (dropiIntegration) {
          setIntegrationsDropi(dropiIntegration);
        }

        if (mastershopIntegration) {
          setIntegrationsMastershop(mastershopIntegration);
        }

        methodsStatus.current = paymentConfig;

        const channelStatusMap = new Map<string, ChannelAccountStatus>(
          normalizeChannelList(channelsOverview?.channels).map((channel) => [
            String(channel.channel),
            channel,
          ])
        );

        const resolveStateFor = (id: IntegrationType) => {
          if (CHAT_CHANNEL_TYPES.includes(id as (typeof CHAT_CHANNEL_TYPES)[number])) {
            return resolveChannelState(channelStatusMap.get(id));
          }
          if (id === "meta") {
            const active = metaIntegration?.is_active ?? false;
            return { isActive: active, isConnected: active, alreadyConfigured: active };
          }
          if (id === "confio") {
            return resolveConfioState(confioIntegration);
          }
          return resolveIntegrationState(id, dropiIntegration, mastershopIntegration);
        };

        const dbIntegrations = availabilityApi
          ? resolveAvailableIntegrations({
              apiIntegrations: availabilityApi as IntegrationAvailabilityApi[],
              country,
            })
              .filter((integration) => isIntegrationVisible(integration.id, experiments))
              .map((integration) => ({
                ...INTEGRATIONS_UI_CONFIG[integration.id],
                ...resolveStateFor(integration.id),
                type: integration.id,
              }))
          : [];

        const visibleTypes = new Set(dbIntegrations.map((integration) => integration.type));
        const extraCards: IntegrationCardState[] = [];

        CHAT_CHANNEL_TYPES.forEach((channelType) => {
          if (!visibleTypes.has(channelType)) {
            extraCards.push({
              ...INTEGRATIONS_UI_CONFIG[channelType],
              ...resolveStateFor(channelType),
            });
          }
        });

        if (!visibleTypes.has("meta") && isIntegrationVisible("meta", experiments)) {
          const active = metaIntegration?.is_active ?? false;
          extraCards.push({
            ...INTEGRATIONS_UI_CONFIG.meta,
            isActive: active,
            isConnected: active,
            alreadyConfigured: active,
          });
        }

        const nextIntegrations = [...dbIntegrations, ...extraCards];
        integrationsSnapshot.set(cacheKey, nextIntegrations);
        setIntegrations(nextIntegrations);
      } catch (error) {
        console.error("Unexpected error loading integrations:", error);
        setIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [cacheKey, country, ownerId, resolveIntegrationState]);

  return {
    integrations,
    loading,
  };
};
